// Widget registry — maps stable backend widget IDs to their React render
// functions. Keep this in sync with backend/routes/user_dashboards.py.
//
// Each widget receives:
//   ctx: { setActiveTab, setShowQuickCapture, setShowClientIntake, navigate }
//   size: 'S' | 'M' | 'L'
//
// We render the EXACT components that were on the Home tab previously — this
// way the customiser is a pure layout layer, no behaviour changes.

import React, { Suspense, lazy } from 'react';
import DeferredMount from '@/components/shared/DeferredMount';
import WelcomeBanner from '@/components/dashboard/WelcomeBanner';
import QuickActionsBar from '@/components/dashboard/QuickActionsBar';
import DashboardExtras from '@/components/dashboard/DashboardExtras';
import ReviewsDueTile from '@/components/dashboard/ReviewsDueTile';
import TodaysAgenda from '@/components/dashboard/TodaysAgenda';
import RecentActivityFeed from '@/components/dashboard/RecentActivityFeed';
import FeeSummary from '@/components/dashboard/FeeSummary';
import ServiceLineWorkspaces from '@/components/dashboard/ServiceLineWorkspaces';
import FamilyHealthWidget from '@/components/dashboard/FamilyHealthWidget';
import IntakeCtaWidget from '@/components/dashboard/IntakeCtaWidget';
import LeadPipelineSummaryWidget from '@/components/dashboard/LeadPipelineSummaryWidget';
import ActiveEngagementsWidget from '@/components/dashboard/ActiveEngagementsWidget';
import TotalAumWidget from '@/components/dashboard/TotalAumWidget';

const MorningBriefingTile = lazy(() => import('@/components/dashboard/MorningBriefingTile'));
const WeeklyDigestTile = lazy(() => import('@/components/dashboard/WeeklyDigestTile'));
const ComplianceWatcherTile = lazy(() => import('@/components/dashboard/ComplianceWatcherTile'));

// Render functions take the shared dashboard ctx and return JSX.
export const WIDGET_RENDERERS = {
  'welcome': (ctx) => (
    <WelcomeBanner
      onNavigate={(tab) => ctx.setActiveTab(tab === 'tasks' ? 'my-tasks' : tab)}
      showLeads={ctx.showLeads !== false}
      orgType={ctx.orgType}
    />
  ),
  'morning-brief': () => (
    <DeferredMount delay={200}>
      <Suspense fallback={null}>
        <MorningBriefingTile />
      </Suspense>
    </DeferredMount>
  ),
  'family-health': (ctx) => <FamilyHealthWidget ctx={ctx} />,
  'intake-cta': (ctx) => <IntakeCtaWidget ctx={ctx} />,
  'lead-pipeline-summary': (ctx) => <LeadPipelineSummaryWidget ctx={ctx} />,
  'active-engagements': (ctx) => <ActiveEngagementsWidget ctx={ctx} />,
  'total-aum': () => <TotalAumWidget />,
  'quick-actions': (ctx) => (
    <QuickActionsBar
      onNewTask={() => ctx.setShowQuickCapture(true)}
      onNewLead={() => ctx.setActiveTab('leads')}
      onNavigate={(target) => {
        if (target === 'templates' || target === 'automation') ctx.setActiveTab('my-tasks');
      }}
      showLeads={ctx.showLeads !== false}
    />
  ),
  'dashboard-extras': (ctx) => (
    <DashboardExtras
      onNavigateToTab={(tab) => ctx.setActiveTab(tab)}
      showLeads={ctx.showLeads !== false}
    />
  ),
  'weekly-digest': () => (
    <DeferredMount delay={500}>
      <Suspense fallback={null}>
        <WeeklyDigestTile />
      </Suspense>
    </DeferredMount>
  ),
  'reviews-due': () => <ReviewsDueTile />,
  'compliance': () => (
    <DeferredMount delay={700}>
      <Suspense fallback={null}>
        <ComplianceWatcherTile />
      </Suspense>
    </DeferredMount>
  ),
  'service-lines': () => <ServiceLineWorkspaces />,
  'todays-agenda': (ctx) => <TodaysAgenda onNavigateToTasks={() => ctx.setActiveTab('my-tasks')} />,
  'recent-activity': () => <RecentActivityFeed limit={15} />,
  'fee-summary': (ctx) => <FeeSummary orgType={ctx.orgType} />,
};

// Side-by-side pairing — when both widgets in a pair are visible AND set to size 'M',
// they render in a two-column grid like the original layout. Otherwise they stack.
export const SIDE_BY_SIDE_PAIRS = [
  ['reviews-due', 'compliance'],
  ['todays-agenda', 'recent-activity'],
];
